import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useButton, useDispatch, useI18n, useLogger } from '~Hooks';
import {
  UpdateBookingPartySize,
  UpdateBookingPartySizeVariables,
} from './__queries__';
import { Box, Button, GreenButton, WhiteButton, Input } from '~Styles';
import { GetBookings_operativeViewer_bookingsForVenueAndDate } from '~Providers/BookingsProvider/api/__queries__';
import Modal from '~ComponentsImproved/Modal';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Loader from '~ComponentsImproved/Loader';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import * as actions from '~actions';
import { useBookings } from '~Providers/BookingsProvider';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';

const UPDATE_BOOKING_PARTY_SIZE = gql`
  mutation UpdateBookingPartySize($bookingId: ID!, $partySize: Int!) {
    updateBookingPartySize(bookingId: $bookingId, partySize: $partySize) {
      success
      message
      error
      booking {
        id
      }
    }
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ErrorBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorUIRed) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorUIRed) 30%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
  text-align: center;
  animation: ${fadeIn} 0.2s ease-in-out forwards;
`;

const CancelButton = styled(Button)`
  max-width: 160px;
  white-space: nowrap;
`;

// 1-15 as quick-select cells, 16th cell is a custom input
const GUEST_OPTIONS = Array.from({ length: 15 }, (_, i) => ({
  id: String(i + 1),
  name: String(i + 1),
}));

const GuestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
  width: 100%;
`;

const StyledGuestButton = styled(Button)<{ active: boolean }>`
  min-height: unset;
  min-width: unset;
  padding: 8px;
  border-radius: 8px;
  font-size: 14px;
  text-transform: none;
  color: ${({ active }) => (active ? 'var(--colorBlack)' : 'var(--colorBlueGray200)')};
  background-color: ${({ active }) =>
    active ? 'var(--colorBlueGray200)' : 'var(--colorBlueGray1100)'};
  border: 1px solid ${({ active }) => (active ? 'var(--colorUIBlue)' : 'var(--colorBlueGray900)')};
`;

const CustomGuestInput = styled(Input)<{ active: boolean }>`
  min-height: unset;
  padding: 8px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

interface GuestCellProps {
  value: string;
  label: string;
  active: boolean;
  onSelect: (value: string) => void;
}

const GuestCell: FC<GuestCellProps> = ({ value, label, active, onSelect }) => {
  const button = useButton({
    onClick: () => onSelect(value),
  });

  return (
    <StyledGuestButton {...button} active={active}>
      {label}
    </StyledGuestButton>
  );
};

interface Props {
  booking: GetBookings_operativeViewer_bookingsForVenueAndDate;
  onUpdated?: () => void;
}

const EditPartySize: FC<Props> = ({ booking, onUpdated }) => {
  const { i18n } = useI18n();
  const logger = useLogger('edit-party-size');
  const dispatch = useDispatch();
  const bookings = useBookings();
  const editModal = useModal();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Track the selected party size - initialize from current booking
  const currentSize = String(booking.partySize);
  const isCurrentSizeInGrid = booking.partySize >= 1 && booking.partySize <= 15;

  const [selectedSize, setSelectedSize] = useState<string | null>(currentSize);
  const [customGuests, setCustomGuests] = useState(isCurrentSizeInGrid ? '' : currentSize);
  const [isCustomActive, setIsCustomActive] = useState(!isCurrentSizeInGrid);

  const [updateBookingPartySize] = useMutation<
    UpdateBookingPartySize,
    UpdateBookingPartySizeVariables
  >(UPDATE_BOOKING_PARTY_SIZE);

  const editButton = useButton({
    onClick: () => {
      // Reset state when opening the modal
      setError(null);
      setSelectedSize(currentSize);
      setIsCustomActive(!isCurrentSizeInGrid);
      setCustomGuests(isCurrentSizeInGrid ? '' : currentSize);
      editModal.toggle();
    },
  });

  const cancelButton = useButton({
    onClick: editModal.toggle,
  });

  const handleSave = async () => {
    if (!selectedSize) return;

    const newPartySize = Number(selectedSize);
    if (isNaN(newPartySize) || newPartySize < 1) return;

    // Don't save if unchanged
    if (newPartySize === booking.partySize) {
      editModal.toggle();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await updateBookingPartySize({
        variables: {
          bookingId: booking.id,
          partySize: newPartySize,
        },
      });

      if (!res.data?.updateBookingPartySize?.success) {
        throw new Error(
          res.data?.updateBookingPartySize?.error ||
            res.data?.updateBookingPartySize?.message ||
            i18n('Bookings.ErrorUpdatingPartySize'),
        );
      }

      logger.info('Booking party size updated', {
        bookingId: booking.id,
        oldPartySize: booking.partySize,
        newPartySize,
      });

      dispatch(actions.addToast(i18n('Bookings.PartySizeUpdated'), true));

      // Refetch bookings
      if (booking.date) {
        bookings.fetchBookingsForDate(booking.date);
      }

      onUpdated?.();
      editModal.toggle();
    } catch (err) {
      logger.error(`Failed to update booking party size: ${err ? err.toString() : ''}`);
      setError(err ? err.toString() : i18n('Bookings.ErrorUpdatingPartySize'));
    } finally {
      setLoading(false);
    }
  };

  const saveButton = useButton({
    onClick: handleSave,
    disabled: loading || !selectedSize,
  });

  return (
    <>
      <WhiteButton {...editButton}>{i18n('Bookings.ChangePartySize')}</WhiteButton>

      <Modal isShowing={editModal.isShowing} hide={editModal.toggle} backdropClose={true}>
        <ModalWrapper>
          <Box fluid>
            <Paragraph level={4} color="var(--colorBlueGray200)" bottom={8}>
              {i18n('Bookings.GuestsLabel')}
            </Paragraph>
            <GuestGrid>
              {GUEST_OPTIONS.map((option) => (
                <GuestCell
                  key={option.id}
                  value={option.id}
                  label={option.name}
                  active={!isCustomActive && selectedSize === option.id}
                  onSelect={(val) => {
                    setIsCustomActive(false);
                    setCustomGuests('');
                    setSelectedSize(val);
                  }}
                />
              ))}
              <CustomGuestInput
                active={isCustomActive}
                dark
                type="number"
                min={1}
                max={100}
                placeholder="16+"
                value={customGuests}
                onFocus={() => setIsCustomActive(true)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = e.target.value;
                  setCustomGuests(val);
                  setIsCustomActive(true);
                  const num = Number(val);
                  if (num >= 1 && num <= 100) {
                    setSelectedSize(val);
                  } else {
                    setSelectedSize(null);
                  }
                }}
              />
            </GuestGrid>
          </Box>

          <Box display="flex" flexDirection="row" gap="small" alignItems="center" fluid>
            <CancelButton {...cancelButton}>{i18n('General.Cancel')}</CancelButton>
            <GreenButton {...saveButton} fluid>
              {loading ? (
                <Loader size="sm" color="var(--colorWhite)" />
              ) : (
                i18n('Bookings.SavePartySize')
              )}
            </GreenButton>
          </Box>

          {error && (
            <ErrorBox fluid display="flex" alignItems="center" justifyContent="center">
              <Paragraph level={5} bottom={0} color="var(--colorUIRed)">
                {error}
              </Paragraph>
            </ErrorBox>
          )}
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default EditPartySize;
